import MoreIcon from '@mui/icons-material/MoreHoriz'
import { Box, IconButton, Link, Typography } from '@mui/material'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import axios from 'axios'
import config from 'config'
import getOption, { OPTION_ALL_VALUE } from 'lib/getOption'
import logger from 'logger'
import T from 'prop-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebouncedCallback } from 'use-debounce'
import { ROLE_SUPER_ADMIN, useAuth } from 'auth'
import { camelCase } from 'lodash'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'

import DateCell from 'components/atoms/DateCell'
import DotCell from 'components/atoms/DotCell'
import FilterDateRange from 'components/atoms/FilterDateRange'
import FilterSelect from 'components/atoms/FilterSelect'
import SearchField from 'components/atoms/SearchField'
import Table from 'components/atoms/Table'
import TableHeaderCell from 'components/atoms/TableHeaderCell'

import useConfirmationDialog from 'hooks/useConfirmationDialog'
import useEntityData from 'hooks/useEntityData'
import useQuery from 'hooks/useQuery'

import ConfirmationDialog from '../../atoms/ConfirmationDialog'
import useSnackbar from '../Snackbar/useSnackbar'
import {
  PARTNER_DOCUMENTS_TYPES,
  PARTNER_DOCUMENTS_TYPES_CAMEL_CASE,
} from '../PartnerDocuments/usePartnerDocuments'

import PartnerInfoDialog from './PartnerInfoDialog'
import UserInfoDialog from './UserInfoDialog'
import UsersHeader from './UsersHeader'
import useUsersFilters from './useUsersFilters'
import {
  USERS_ACCOUNT_TYPE_PARTNER_USERS_LIST,
  USERS_ACCOUNT_TYPE_SIXT_USERS_LIST,
  USERS_LIST_TYPE_PARTNER_USERS,
  USERS_LIST_TYPE_PARTNERS,
  USERS_LIST_TYPE_SIXT_USERS,
} from './usersLib'

const StatusRenderer = (value, t) => {
  return (
    <DotCell
      color={value === 'enabled' ? 'primary' : 'error'}
      label={t(`users.status.${value}`)}
    />
  )
}

const ReceiveEmailDisabled = (value, t) => {
  return (
    <DotCell
      // color={value === false ? 'primary' : 'error'}
      label={t(`users.receiveEmailDisabled.${value ? 'disabled' : 'enabled'}`)}
    />
  )
}

const pathToAccountTypeFilter = {
  sixtUsers: USERS_ACCOUNT_TYPE_SIXT_USERS_LIST,
  partnerUsers: USERS_ACCOUNT_TYPE_PARTNER_USERS_LIST,
}

const COMMON_FORM_SEARCH = 'common.form.search'
const UsersContent = ({
  enabledListTypes,
  defaultType,
  contentIsReadOnly = false,
  inTest = false,
}) => {
  const { t } = useTranslation()
  const query = useQuery()
  const { hasOneOfRoles } = useAuth()
  const listType = query.get('type') || defaultType

  const { showSnackbarMessage } = useSnackbar()
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedUser, setSelectedUser] = useState()
  const [selectedPartner, setSelectedPartner] = useState()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [newRowsPerPage, setNewRowsPerPage] = useState(10)
  const [newPage, setNewPage] = useState(0)

  const superAdmin = useMemo(
    () => hasOneOfRoles([ROLE_SUPER_ADMIN]),
    [hasOneOfRoles]
  )

  const {
    initialized,
    filters,
    filtersData,
    setEditorQuery,
    setNameQuery,
    setSurnameQuery,
    setEmailQuery,
    setCreatedAtRange,
    setUpdatedAtRange,
    setPartners,
    setAccount,
    setStates,
    setSort,
    initFilters,
    setGenericFilter,
  } = useUsersFilters(listType)

  const {
    openConfirmationDialog: openDocumentsToDownloadDialog,
    handleOpenConfirmationDialog: handleOpenDocumentsToDownloadDialog,
    handleCloseConfirmationDialog: handleCloseDocumentsToDownloadDialogOriginal,
  } = useConfirmationDialog(() => {})

  const onMenuClick = useCallback(
    (event, user) => {
      setAnchorEl(event.currentTarget)

      if (listType === USERS_LIST_TYPE_PARTNERS) {
        setSelectedPartner(user)
      } else {
        setSelectedUser(user)
      }
    },
    [listType]
  )

  const handleChangePage = (_, newPage) => {
    setNewPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setNewRowsPerPage(parseInt(event.target.value, 10))
    setNewPage(0)
  }

  const handleResetMFA = useCallback(async () => {
    try {
      await axios.post(
        `${config.apiBaseUrl}users/reset-mfa/${selectedUser.userId}`,
        {}
      )

      showSnackbarMessage({
        severity: 'success',
        message: t('users.mfaDialog.success'),
      })
    } catch (e) {
      logger.error('[Error] - partner creation', e)
      showSnackbarMessage({
        severity: 'error',
        message: t('users.mfaDialog.error'),
      })
    }
  }, [selectedUser, showSnackbarMessage, t])

  const {
    openConfirmationDialog,
    handleConfirmConfirmationDialog,
    handleOpenConfirmationDialog,
    handleCloseConfirmationDialog,
  } = useConfirmationDialog(handleResetMFA)

  const onOpenDocumentsToDownloadDialog = useCallback(
    (event, partner) => {
      setSelectedPartner(partner)
      handleOpenDocumentsToDownloadDialog()
    },
    [handleOpenDocumentsToDownloadDialog]
  )

  const handleCloseDocumentsToDownloadDialog = () => {
    setSelectedPartner(null)
    handleCloseDocumentsToDownloadDialogOriginal()
  }
  const actionCell = useCallback(
    (props) => {
      if (!!contentIsReadOnly) {
        return <></>
      } else {
        return (
          <IconButton
            aria-label="More"
            onClick={(e) => onMenuClick(e, props.row.original)}
            data-testid={`action-${props.row.id}`}
          >
            <MoreIcon />
          </IconButton>
        )
      }
    },
    [onMenuClick, contentIsReadOnly]
  )

  const usersColumns = useMemo(() => {
    if (
      !filters?.initialized ||
      ![USERS_LIST_TYPE_SIXT_USERS, USERS_LIST_TYPE_PARTNER_USERS].includes(
        listType
      )
    )
      return []

    const allOption = getOption(t('common.form.all'), OPTION_ALL_VALUE)

    return [
      {
        accessor: 'firstName',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.name')}
            filter={
              <SearchField
                placeholder={t(COMMON_FORM_SEARCH)}
                value={filters?.nameQuery}
                onChange={(event) => setNameQuery(event.target.value)}
                fullWidth={false}
                sx={{ width: (theme) => theme.spacing(15) }}
                data-testid="filter-nameQuery"
              />
            }
          />
        ),
      },
      {
        accessor: 'lastName',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.surname')}
            filter={
              <SearchField
                placeholder={t(COMMON_FORM_SEARCH)}
                value={filters?.surnameQuery}
                onChange={(event) => setSurnameQuery(event.target.value)}
                fullWidth={false}
                sx={{ width: (theme) => theme.spacing(15) }}
                data-testid="filter-surnameQuery"
              />
            }
          />
        ),
      },
      {
        accessor: 'partner.name',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.partner')}
            filter={
              <FilterSelect
                allOption={allOption}
                options={filtersData.partners}
                value={filters.partners}
                handleChange={setPartners}
                multiple
                hasSearchField
                data-testid="filter-partner"
              />
            }
          />
        ),
      },
      {
        accessor: 'email',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.email')}
            filter={
              <SearchField
                placeholder={t(COMMON_FORM_SEARCH)}
                value={filters?.emailQuery}
                onChange={(event) => setEmailQuery(event.target.value)}
                fullWidth={false}
                sx={{
                  width: (theme) => theme.spacing(15),
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                data-testid="filter-emailQuery"
              />
            }
          />
        ),
        width: 300,
      },
      {
        accessor: 'createdAt',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.createdAt')}
            filter={
              <FilterDateRange
                value={[filters.createdAtFrom, filters.createdAtTo]}
                handleChange={setCreatedAtRange}
                data-testid="filter-createdAt"
              />
            }
          />
        ),
        Cell: DateCell,
      },
      {
        accessor: 'updatedAt',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.lastEdit')}
            filter={
              <FilterDateRange
                value={[filters.updatedAtFrom, filters.updatedAtTo]}
                handleChange={setUpdatedAtRange}
                data-testid="filter-updatedAt"
              />
            }
          />
        ),
        Cell: DateCell,
      },
      {
        accessor: 'lastEditorEmail',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.editor')}
            filter={
              <SearchField
                placeholder={t(COMMON_FORM_SEARCH)}
                value={filters?.editorQuery}
                onChange={(event) => setEditorQuery(event.target.value)}
                fullWidth={false}
                sx={{ width: (theme) => theme.spacing(15) }}
                data-testid="filter-editorQuery"
              />
            }
          />
        ),
      },
      {
        accessor: 'accountType',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.accountType')}
            filter={
              <FilterSelect
                allOption={allOption}
                options={filtersData.account}
                value={filters.account}
                handleChange={setAccount}
                multiple
                data-testid="filter-account"
              />
            }
          />
        ),
      },
      {
        accessor: 'state',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.status')}
            filter={
              <FilterSelect
                allOption={allOption}
                options={filtersData.states}
                value={filters.states}
                handleChange={setStates}
                data-testid="filter-state"
              />
            }
          />
        ),
        Cell: ({ value }) => StatusRenderer(value, t),
      },
      {
        accessor: 'language',
        Header: <TableHeaderCell label={t('users.tableColumns.language')} />,
        Cell: ({ value }) => t(`users.languages.${value}`),
      },
      {
        accessor: 'receiveEmailDisabled',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.receiveEmailDisabled')}
          />
        ),
        Cell: ({ value }) => ReceiveEmailDisabled(value, t),
      },
      {
        id: 'options',
        Cell: actionCell,
      },
    ]
  }, [
    filters,
    filtersData,
    listType,
    setAccount,
    setCreatedAtRange,
    setEditorQuery,
    setEmailQuery,
    setNameQuery,
    setPartners,
    setStates,
    setSurnameQuery,
    setUpdatedAtRange,
    t,
    actionCell,
  ])

  const partnersColumns = useMemo(() => {
    if (!filters?.initialized || ![USERS_LIST_TYPE_PARTNERS].includes(listType))
      return []

    const allOption = getOption(t('common.form.all'), OPTION_ALL_VALUE)

    const hasDocumentsToDownload = (partner) => {
      return PARTNER_DOCUMENTS_TYPES_CAMEL_CASE.some(
        (type) => !!partner?.[`${camelCase(type)}AddedAt`]
      )
    }

    return [
      {
        accessor: 'name',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.partnerName')}
            filter={
              <SearchField
                placeholder={t(COMMON_FORM_SEARCH)}
                value={filters?.partnerNameQuery}
                onChange={(event) =>
                  setGenericFilter('partnerNameQuery', event.target.value)
                }
                fullWidth={false}
                sx={{ width: (theme) => theme.spacing(15) }}
              />
            }
          />
        ),
        cellProps: {
          sx: {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '250px',
          },
        },
        sticky: 'left',
      },
      {
        accessor: 'partnerId',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.partnerId')}
            filter={
              <SearchField
                placeholder={t(COMMON_FORM_SEARCH)}
                value={filters?.partnerIdQuery}
                onChange={(event) =>
                  setGenericFilter('partnerIdQuery', event.target.value)
                }
                fullWidth={false}
                sx={{ width: (theme) => theme.spacing(15) }}
              />
            }
          />
        ),
        cellProps: {
          sx: {
            maxWidth: '200px',
          },
        },
      },
      {
        accessor: 'address',
        Header: <TableHeaderCell label={t('users.tableColumns.address')} />,
        cellProps: {
          sx: {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            // maxWidth: '200px',
          },
        },
        Cell: ({ value }) => value || '',
      },
      {
        accessor: 'vat',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.vat')}
            filter={
              <SearchField
                placeholder={t(COMMON_FORM_SEARCH)}
                value={filters?.vatQuery}
                onChange={(event) =>
                  setGenericFilter('vatQuery', event.target.value)
                }
                fullWidth={false}
              />
            }
          />
        ),
        cellProps: {
          sx: {
            maxWidth: '200px',
          },
        },
      },
      {
        accessor: 'documentsToDownload',
        Header: (
          <TableHeaderCell
            label={t('users.tableColumns.documentsToDownload')}
          />
        ),
        Cell: ({ value, row }) =>
          hasDocumentsToDownload(row.original) && (
            <IconButton
              data-testid="documents-to-download"
              aria-label="Documents to download"
              onClick={(e) => onOpenDocumentsToDownloadDialog(e, row.original)}
            >
              <DescriptionOutlinedIcon />
            </IconButton>
          ),
        cellProps: {
          sx: {
            maxWidth: '100px',
          },
        },
      },

      ...(superAdmin
        ? [
            {
              accessor: 'createdAt',
              Header: (
                <TableHeaderCell
                  label={t('users.tableColumns.createdAt')}
                  filter={
                    <FilterDateRange
                      value={[filters.createdAtFrom, filters.createdAtTo]}
                      handleChange={setCreatedAtRange}
                    />
                  }
                />
              ),
              Cell: DateCell,
            },
            {
              accessor: 'updatedAt',
              Header: (
                <TableHeaderCell
                  label={t('users.tableColumns.lastEdit')}
                  filter={
                    <FilterDateRange
                      value={[filters.updatedAtFrom, filters.updatedAtTo]}
                      handleChange={setUpdatedAtRange}
                    />
                  }
                />
              ),
              Cell: DateCell,
            },
            {
              accessor: 'lastEditorEmail',
              Header: (
                <TableHeaderCell
                  label={t('users.tableColumns.editor')}
                  filter={
                    <SearchField
                      placeholder={t(COMMON_FORM_SEARCH)}
                      value={filters?.editorQuery}
                      onChange={(event) =>
                        setGenericFilter('editorQuery', event.target.value)
                      }
                      fullWidth={false}
                      sx={{
                        width: (theme) => theme.spacing(15),
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    />
                  }
                />
              ),
              width: 300,
            },
            {
              accessor: 'state',
              Header: (
                <TableHeaderCell
                  label={t('users.tableColumns.status')}
                  filter={
                    <FilterSelect
                      allOption={allOption}
                      options={filtersData.states}
                      value={filters.states}
                      handleChange={setStates}
                    />
                  }
                />
              ),
              Cell: ({ value }) => StatusRenderer(value, t),
            },
            {
              id: 'options',
              Cell: actionCell,
            },
          ]
        : []),
    ]
  }, [
    actionCell,
    filters?.createdAtFrom,
    filters?.createdAtTo,
    filters?.editorQuery,
    filters?.initialized,
    filters?.partnerIdQuery,
    filters?.partnerNameQuery,
    filters?.states,
    filters?.updatedAtFrom,
    filters?.updatedAtTo,
    filters?.vatQuery,
    filtersData.states,
    listType,
    onOpenDocumentsToDownloadDialog,
    setCreatedAtRange,
    setGenericFilter,
    setStates,
    setUpdatedAtRange,
    superAdmin,
    t,
  ])

  const handleCloseMenu = () => {
    setAnchorEl(null)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setSelectedUser()
  }

  const onEditClick = () => {
    setDialogOpen(true)
  }

  const prepareFilters = useCallback(
    (filters = {}, filtersData = {}) => {
      return {
        ...filters,
        accountType: pathToAccountTypeFilter[listType],
      }
    },
    [listType]
  )

  useEffect(() => {
    setNewPage(0)
  }, [listType, filters])

  const {
    items,
    isFetching,
    total,
    isFetchingOtherPages,
    page,
    rowsPerPage,
    refetch,
  } = useEntityData({
    newPage,
    limit: newRowsPerPage,
    filters: JSON.stringify(prepareFilters(filters, filtersData)),
    sort: filters?.order?.column,
    direction: filters?.order?.direction,
  })

  useEffect(() => {
    if (!inTest) return
    refetch(listType === USERS_LIST_TYPE_PARTNERS ? 'partners' : 'users')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFilters = useDebouncedCallback(async () => {
    refetch(listType === USERS_LIST_TYPE_PARTNERS ? 'partners' : 'users')
  }, 100)

  useEffect(() => {
    if (!initialized) return
    handleFilters()
  }, [refetch, newPage, newRowsPerPage, handleFilters, initialized])

  useEffect(() => {
    if (initialized) {
      return
    }
    initFilters(listType)
  }, [items, initFilters, initialized, listType])

  const columns =
    listType === USERS_LIST_TYPE_PARTNERS ? partnersColumns : usersColumns

  return (
    <>
      <UsersHeader
        onNew={() => {
          if (contentIsReadOnly === false) {
            setSelectedUser(null)
            setSelectedPartner(null)
            setDialogOpen(true)
          }
        }}
        listType={listType}
        enabledListTypes={enabledListTypes}
        contentIsReadOnly={contentIsReadOnly}
      />

      {initialized && !!columns.length && (
        <Table
          items={items}
          columns={columns}
          isFetching={isFetching}
          isFetchingOtherPages={isFetchingOtherPages}
          total={total}
          page={page}
          rowsPerPage={rowsPerPage}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          hasPagination={true}
          rowsPerPageOptions={[10, 20, 30, 50]}
          sortable
          excludedSortableIds={['options']}
          orderColumn={filters?.order?.column}
          orderDirection={filters?.order?.direction}
          handleSort={setSort}
        />
      )}

      {contentIsReadOnly === false && (
        <Menu
          data-testid={`action-menu`}
          id="options-menu"
          anchorEl={anchorEl}
          open={!!anchorEl}
          onClose={handleCloseMenu}
        >
          <MenuItem
            data-testid={`action-menu-edit`}
            onClick={() => {
              if (contentIsReadOnly === false) {
                onEditClick()
                handleCloseMenu()
              }
            }}
          >
            {t('users.menu.edit')}
          </MenuItem>
          {listType !== USERS_LIST_TYPE_PARTNERS && (
            <MenuItem
              data-testid={`action-menu-reset-mfa`}
              onClick={() => {
                if (contentIsReadOnly === false) {
                  setSelectedUser(selectedUser)
                  handleOpenConfirmationDialog()
                  handleCloseMenu()
                }
              }}
            >
              Reset MFA
            </MenuItem>
          )}
        </Menu>
      )}

      {listType === USERS_LIST_TYPE_PARTNERS ? (
        <PartnerInfoDialog
          open={dialogOpen}
          partner={selectedPartner}
          handleClose={handleCloseDialog}
          refetch={refetch.bind(null, 'partners')}
        />
      ) : (
        <UserInfoDialog
          open={dialogOpen}
          user={selectedUser}
          handleClose={handleCloseDialog}
          refetch={refetch.bind(null, 'users')}
          allowedRoles={pathToAccountTypeFilter[listType]}
        />
      )}
      <ConfirmationDialog
        title={t('users.mfaDialog.title')}
        content={<>{t('users.mfaDialog.content')}</>}
        open={openConfirmationDialog}
        handleClose={handleCloseConfirmationDialog}
        handleSubmit={handleConfirmConfirmationDialog}
      />
      {listType === USERS_LIST_TYPE_PARTNERS && (
        <>
          <ConfirmationDialog
            open={openDocumentsToDownloadDialog}
            title={t(`users.documentsToDownloadDialog.title`)}
            handleClose={handleCloseDocumentsToDownloadDialog}
            confirmLabel={t('common.form.close')}
            handleSubmit={handleCloseDocumentsToDownloadDialog}
            hideCancel
            contentComponent={Box}
            content={
              <>
                <Typography>
                  {t('users.documentsToDownloadDialog.content')}
                </Typography>
                <Box pb={2} />
                {selectedPartner &&
                  PARTNER_DOCUMENTS_TYPES.map((type, index) => {
                    return (
                      <React.Fragment key={`${type}`}>
                        {Array.from({ length: 2 }, (_, i) => i + 1).map((i) => {
                          const documentName =
                            selectedPartner[
                              `${PARTNER_DOCUMENTS_TYPES_CAMEL_CASE[index]}Document${i}`
                            ]
                          return documentName ? (
                            <React.Fragment key={`${type}-document-${i}`}>
                              <Link
                                component={Box}
                                variant="body2"
                                onClick={async () => {
                                  const result = await axios.get(
                                    `${config.apiBaseUrl}partners/sign-download-document?filename=${selectedPartner.partnerId}/${documentName}`
                                  )
                                  window.location = result.data?.body?.signedUrl
                                }}
                                color="primary"
                                sx={{
                                  cursor: 'pointer',
                                }}
                              >
                                {t(
                                  `partnerDocuments.types.${type}.document${i}.label`
                                )}
                              </Link>
                              <Box pb={1} />
                            </React.Fragment>
                          ) : null
                        })}
                      </React.Fragment>
                    )
                  })}
              </>
            }
          />
        </>
      )}
    </>
  )
}

export default UsersContent

UsersContent.propTypes = {
  enabledListTypes: T.arrayOf(T.string),
  selectedSessionId: T.string,
  contentIsReadOnly: T.bool,
}
